import { FieldError, FieldErrors, FieldValues } from 'react-hook-form'

const toTokens = (path: string) => path
  .replace(/\[(\d+)]/g, '.$1')
  .split('.')
  .filter(Boolean)

// RHF's docs require a resolver's errors to be nested to match field names rather than flat
// errors['a.0.b'] keys, so that error clearing and state management work; FieldErrors<T> cannot
// express a flat key either. A flat key does still reach its input, but only via an undocumented
// fallback in RHF's `get` (it retries object[path] when the nested walk comes up empty).
// Places the error the way form.setError does, and leaves an existing error at the path alone,
// so a zod error already at this path wins and schema mismatches stay visible.
// Containers are identified by position in the path, never by shape: form objects carry fields
// named `type` and `message` (additionalReference.*.type, placeOfLoading.type), so a node that
// looks like a FieldError may be the parent of the error being set.
function setFieldErrorByPath<TFieldValues extends FieldValues>(
  errors: FieldErrors<TFieldValues>,
  path: string,
  error: FieldError,
) {
  const tokens = toTokens(path)
  if (!tokens.length) return

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let current: any = errors

  for (let i = 0; i < tokens.length - 1; i += 1) {
    const token = tokens[i]

    if (current[token] == null) {
      current[token] = /^\d+$/.test(tokens[i + 1]) ? [] : {}
    } else if (typeof current[token] !== 'object') {
      return
    }

    current = current[token]
  }

  const leaf = tokens[tokens.length - 1]
  if (current[leaf] == null) {
    current[leaf] = error
  }
}

export default setFieldErrorByPath
